import imgTurns from "@assets/web-v3-images/turns-img.png";
import iconError from "@assets/web-v3-images/not-lowest-unique.png";
import iconSuccess from "@assets/web-v3-images/icon-success.png";
import iconConfirm from "@assets/web-v3-images/icon-confirm.png";
import { Text } from "@radix-ui/themes";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Buttons from "../Buttons";
import PopupFrame from ".";
import ItemPackage from "@/component/ItemPackage";
import ModalNotifycation from "@/component/ModalNotifycation";
import PATH from "@config/PATH";
import { curStateAccount } from "@/Redux/selector";
import {
  getListPackage,
  getTurnRemain,
  registerRetailPackage,
  registerSubPackage,
} from "@/Redux/futures/account/actions";

/* ItemPackage hardcode kích thước px theo canvas design 880px của trang Package
   (xem UseResponsive), trong đó vùng đặt item rộng 880 - 2×32 = 816px. Muốn item
   trong popup ra ĐÚNG tỉ lệ đó thì phải dựng lại y hệt bối cảnh: render ở bề rộng
   design cố định rồi scale xuống bề rộng thật.
   Cộng thêm PAD mỗi bên vì nhãn Best Seller của ItemPackage thò ra right:-17px —
   khung nội dung PopupFrame có overflow-y-auto (kéo theo trục ngang cũng không
   còn `visible`) nên thò khỏi mép là bị cắt. */
const DESIGN_ITEM_W = 816;
const DESIGN_PAD = 24;
const DESIGN_W = DESIGN_ITEM_W + DESIGN_PAD * 2;

/**
 * Render children ở bề rộng design cố định rồi thu nhỏ về bề rộng thật — cùng cơ
 * chế với UseResponsive của trang Package, nên mọi px hardcode bên trong giữ
 * nguyên tỉ lệ. `transform` không chiếm chỗ trong layout nên phải tự gán chiều
 * cao cho khung ngoài = chiều cao thật × scale, không thì phần dưới bị chồng lên.
 */
const DesignScale = ({ children }) => {
  const hostRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const content = contentRef.current;
    if (!host || !content) return;
    const measure = () => {
      setScale(host.clientWidth / DESIGN_W);
      setContentHeight(content.offsetHeight);
    };
    measure();
    // Theo dõi cả hai: host đổi khi popup/viewport co giãn, content đổi khi danh
    // sách gói về hoặc đổi ngôn ngữ (chuỗi Myanmar dài hơn -> item cao hơn).
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={hostRef} style={{ height: contentHeight * scale }}>
      <div
        ref={contentRef}
        style={{
          // border-box để padding nằm TRONG DESIGN_W -> phần ruột đúng 816px,
          // bằng đúng bề rộng item bên trang Package.
          boxSizing: "border-box",
          width: DESIGN_W,
          padding: `0 ${DESIGN_PAD}px`,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
          // scale khởi tạo là 0 -> ẩn cho tới khi đo xong, tránh nháy một khung
          // rộng 864px tràn popup ở frame đầu.
          visibility: scale ? "visible" : "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default function OutOfTurns({ open, onOpenChange = () => { }, hiddenText = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectorAccount = useSelector(curStateAccount);
  const isBuyingRef = useRef(false);

  // Mở popup -> gọi API packs lấy danh sách gói (giống PackagePage).
  useEffect(() => {
    if (open) dispatch(getListPackage({}));
  }, [open, dispatch]);

  /* Sub = gói CÓ cycle, retail = gói KHÔNG có cycle (cùng cách lọc PackagePage). */
  const { subPacks, retailPacks } = useMemo(() => {
    const list = selectorAccount?.listPackage || [];
    return {
      subPacks: list.filter((it) => it.cycle),
      retailPacks: list.filter((it) => !it.cycle),
    };
  }, [selectorAccount?.listPackage]);

  /* Đã đăng ký sub hay chưa: remain-turn có bản ghi trùng pack_id với một gói
     sub nào đó (cùng cách nhận biết PackagePage đang dùng). Tính suy ra từ store
     thay vì giữ trong state — sub hết hạn thì cờ tự về false, chứ state một
     chiều sẽ kẹt ở true tới lúc reload. */
  const hasSubPackage = useMemo(
    () =>
      subPacks.some((pack) =>
        (selectorAccount?.remainTurn || []).some(
          (item) => item?.pack_id === pack?.pack_id,
        ),
      ),
    [subPacks, selectorAccount?.remainTurn],
  );


  /* Chưa có sub -> mời đăng ký sub; đã có sub rồi -> chỉ chào bán gói lẻ. */
  const packsToShow = hasSubPackage ? retailPacks : subPacks;

  /* Kết quả mua gói dùng chung ModalNotifycation isVersion3 với trang Package —
     cùng khung popup, cùng icon cô gái, cùng nút.
     Thành công -> đóng OutOfTurns luôn (user đã có lượt, không còn gì để mời mua).
     Lỗi -> GIỮ NGUYÊN OutOfTurns, popup lỗi chồng lên trên; đóng nó là quay lại
     đúng danh sách gói cũ để chọn lại. Hai lớp đều là Radix Dialog nên Radix tự
     xếp chồng: lớp mở sau nằm trên và giữ tương tác, lớp dưới tạm khoá. */
  const [resultPopup, setResultPopup] = useState({
    open: false,
    isError: false,
    title: "",
    content: "",
  });

  const closeResultPopup = () =>
    setResultPopup({ open: false, isError: false, title: "", content: "" });

  const showResultPopup = (isError, content) => {
    if (!isError) onOpenChange(false);
    setResultPopup({
      open: true,
      isError,
      title: isError ? t("error") : t("success"),
      content,
    });
  };

  // Kết quả mua: errorCode 0 -> cập nhật số lượt + popup thành công; còn lại -> popup lỗi.
  const afterBuy = (data, isLoading, dataError) => {
    if (isLoading) return;
    isBuyingRef.current = false;
    if (dataError) {
      showResultPopup(true, dataError?.errorMessage || t("error"));
    } else if (+data?.data?.errorCode === 0) {
      dispatch(getTurnRemain({}));
      showResultPopup(false, data?.data?.content || t("success"));
    } else {
      showResultPopup(true, data?.data?.content || t("error"));
    }
  };

  /* Bấm Buy KHÔNG gọi API ngay: phải qua bước xác nhận như PackagePage
     (handelSelectItem -> handleRegisterPackage). Nội dung confirm_buy là lời báo
     hệ thống sắp gửi USSD về máy, thiếu bước này user không biết phải mở USSD để
     hoàn tất nên đơn treo. Giữ gói đang chọn trong state, API chỉ chạy khi user
     bấm xác nhận. */
  const [confirmPack, setConfirmPack] = useState(null);

  const handleBuy = (pack) => {
    if (!pack?.pack_id || isBuyingRef.current) return;
    setConfirmPack(pack);
  };

  const closeConfirmPopup = () => setConfirmPack(null);

  /* Hai API mua khác nhau, chọn theo loại gói (giống handleRegisterPackage bên
     PackagePage): sub -> registerSubPackage(packId + isRegister), lẻ ->
     registerRetailPackage(selectedPack + giá). */
  const handleConfirmBuy = () => {
    const pack = confirmPack;
    // Popup vẫn mở trong lúc chờ API nên nút xác nhận còn bấm được -> chặn bấm
    // lần 2, không thì user bị trừ tiền hai lần cho cùng một gói.
    if (!pack?.pack_id || isBuyingRef.current) return;
    isBuyingRef.current = true;
    setConfirmPack(null);
    dispatch(
      pack.cycle
        ? registerSubPackage({
          packId: pack.pack_id,
          isRegister: true,
          callback: afterBuy,
        })
        : registerRetailPackage({
          selectedPack: pack.pack_id,
          selectedPackPrice: pack.price,
          callback: afterBuy,
        }),
    );
  };

  // View all -> trang danh sách gói (/account/package).
  const handleViewAll = () => {
    onOpenChange(false);
    navigate(PATH.PACKAGE);
  };

  return (
    <>
      <PopupFrame
        open={open}
        onOpenChange={onOpenChange}
        title={t("out_of_turns.title")}
      >
        <div className="">
          {hiddenText &&
            <Text
              as="p"
              className="text-[#A15C07] font-medium text-[15px] text-center"
            >
              {t("out_of_turns.description")}
            </Text>
          }
          <div className="px-10 sm:px-15 md:px-30 lg:px-30 xl:px-30 pt-5">
            <img src={imgTurns} alt="turns" className="w-full h-full" />
          </div>

          {/* Dùng thẳng ItemPackage của trang Package để bố cục giống hệt: mô tả
            + giá + nút Buy, nhãn Best Seller và cả cách suy ra text theo pack_id
            đều nằm sẵn trong đó. `type` quyết định dòng mô tả thời hạn, còn
            isRegister=false vì gói sub chỉ được liệt kê khi user CHƯA đăng ký. */}
          <div className="mt-7">
            <DesignScale>
              {packsToShow.map((pack) => (
                <ItemPackage
                  key={pack.pack_id}
                  data={pack}
                  type={pack.cycle ? "SUB" : "RETAIL"}
                  isRegister={false}
                  handelSelectItem={handleBuy}
                />
              ))}
            </DesignScale>
          </div>

          <div className="px-[20%]">
            <Buttons
              handleClick={handleViewAll}
              textBtn={t("out_of_turns.view_all")}
            />
          </div>
        </div>
      </PopupFrame>

      {/* Popup xác nhận trước khi gọi API mua — cùng component/nội dung/icon với
        PackagePage (title confirmation, message confirm_buy, icon-confirm), nút
        xác nhận dùng lại nhãn bid_page.confirm. */}
      <ModalNotifycation
        open={!!confirmPack}
        handleCancel={closeConfirmPopup}
        handleOk={handleConfirmBuy}
        title={t("confirmation")}
        message={t("account_page.package.confirm_buy")}
        topIcon={iconConfirm}
        textBtn={t("bid_page.confirm")}
        isVersion3
      />

      {/* Popup kết quả mua gói — cùng component + cùng cặp icon với PackagePage:
        thành công dùng icon-bid-success, lỗi dùng not-lowest-unique + nút Cancel. */}
      <ModalNotifycation
        open={resultPopup.open}
        handleCancel={closeResultPopup}
        title={resultPopup.title}
        message={resultPopup.content}
        topIcon={resultPopup.isError ? iconError : iconSuccess}
        textBtn={resultPopup.isError ? t("bid_page.cancel") : undefined}
        isVersion3
      />
    </>
  );
}
